import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatClock, splitHours } from '@/lib/format';
import type { SessionStatus } from './day-sessions';

/** 线的样式就是状态：正常绿实线、待复核橙线、在岗点线、缺卡红虚线、过长红实线 */
const LINE: Record<SessionStatus, string> = {
  ok: 'border-solid border-ok',
  pending: 'border-solid border-safety-500',
  open: 'border-dotted border-ok',
  missingStart: 'border-dashed border-danger',
  missingEnd: 'border-dashed border-danger',
  tooLong: 'border-solid border-danger',
};

/**
 * 一段班次：`上班时刻 ─── 下班时刻 · 时长`（考勤页 UI 打磨）。
 *
 * 客户原话「同一天分段打卡上下班两个时间最好用一些把两个时间连接对应下」——
 * 两个时刻单独摆着，得反应一会儿才知道哪个对哪个；中间拉一条线，一眼就是一对。
 * 工时表、异常行、补卡表单三处共用。补卡时缺的那一端换成时间框（`startSlot` / `endSlot`）。
 */
export function SessionLine({
  start,
  end,
  status,
  minutes,
  endsNextDay,
  timezone,
  startSlot,
  endSlot,
  now,
}: {
  start?: string | null;
  end?: string | null;
  status: SessionStatus;
  minutes?: number | null;
  endsNextDay?: boolean;
  timezone: string;
  startSlot?: ReactNode;
  endSlot?: ReactNode;
  now?: Date;
}) {
  const { t, i18n } = useTranslation();
  const clock = (iso: string) => formatClock(iso, timezone, i18n.language);

  const missing = (
    <span className="rounded border border-dashed border-danger px-1.5 font-sans text-caption text-danger">
      {t('attendance.notPunched')}
    </span>
  );

  const elapsed =
    status === 'open' && start
      ? Math.max(0, Math.round(((now ?? new Date()).getTime() - new Date(start).getTime()) / 60_000))
      : null;
  const shown = minutes ?? elapsed;
  const parts = shown !== null && shown !== undefined ? splitHours(shown) : null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className={cn('shrink-0 font-mono text-body tabular-nums', status === 'pending' && 'text-safety-600')}>
        {startSlot ?? (start ? clock(start) : missing)}
      </span>
      <span aria-hidden className={cn('h-0 min-w-6 flex-1 border-t-2', LINE[status])} />
      <span className="shrink-0 font-mono text-body tabular-nums">
        {endSlot ??
          (end ? (
            <>
              {clock(end)}
              {endsNextDay && <sup className="ml-0.5 font-sans text-caption text-ink-3">+1</sup>}
            </>
          ) : status === 'open' ? (
            <Badge tone="ok">{t('attendance.onShiftNow')}</Badge>
          ) : (
            missing
          ))}
      </span>
      {parts && (
        <span className="w-28 shrink-0 text-right text-caption tabular-nums text-ink-3">
          {t(status === 'open' ? 'attendance.elapsed' : 'attendance.duration', {
            hours: parts.hours,
            minutes: parts.minutes,
          })}
        </span>
      )}
    </div>
  );
}
